import { Button } from '@mui/material';
import { chatRequest, followUpRequest } from 'api/requests/ai-assistant-requests';
import UPButton from 'components/button';
import LoadingDots from 'components/loading-dots';
import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import {
  AssistantBubble,
  ChatContainer,
  ChatHeader,
  ChatMessagesContainer,
  ChatTextBox,
  ChatTextInput,
  ChatTextSend,
  StyledSendIcon,
  UserBubble,
} from './styles';
import { AssistantChatProps } from './types';

const AssistantChat: React.FC<AssistantChatProps> = ({ onClose }) => {
  const [messages, setMessages] = useState<string[]>([]);
  const [threadId, setThreadId] = useState<string>('');
  const [currentMessage, setCurrentMessage] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const initiateChat = async () => {
      setLoading(true);
      const response = await chatRequest('Hello');
      setMessages([response.message]);
      setThreadId(response.threadId);
      setLoading(false);
    };

    initiateChat();
  }, []);

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async (message: string) => {
    setLoading(true);
    setMessages(prevMessages => [...prevMessages, message]);
    setCurrentMessage('');

    try {
      const response = await followUpRequest({ threadId, message });
      setMessages(prevMessages => [...prevMessages, response.message]);
    } catch (error) {
      setMessages(prevMessages => [...prevMessages, 'An error occured. Please try again.']);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ChatContainer>
      <ChatHeader>
        Assistant Chat
        <Button onClick={onClose}>X</Button>
      </ChatHeader>
      <ChatMessagesContainer>
        {messages.map((message, index) =>
          index % 2 === 0 ? (
            <AssistantBubble key={index.toString()}>{message}</AssistantBubble>
          ) : (
            <UserBubble key={index.toString()}>{message}</UserBubble>
          ),
        )}
        {loading && (
          <AssistantBubble>
            <LoadingDots />
          </AssistantBubble>
        )}
        <div ref={messagesEndRef} />
      </ChatMessagesContainer>
      <ChatTextBox>
        <ChatTextInput
          value={currentMessage}
          onChange={(event: ChangeEvent<HTMLInputElement>) => setCurrentMessage(event.target.value)}
          onKeyPress={(event: React.KeyboardEvent<HTMLInputElement>) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              sendMessage(currentMessage);
            }
          }}
        />
        <ChatTextSend>
          <UPButton text={<StyledSendIcon />} onClick={() => sendMessage(currentMessage)} disabled={loading} />
        </ChatTextSend>
      </ChatTextBox>
    </ChatContainer>
  );
};

export default AssistantChat;
